import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  downloadPlanExcel,
  getCountingPlanReport,
  getCountingPlans,
} from '../../../../api/inventoryCountingApi';

function statusColor(status) {
  if (status === 'CLOSED') return 'success';
  if (status === 'PARTIAL') return 'warning';
  return 'default';
}

export default function PlanList({ refreshKey }) {
  const [plans, setPlans] = useState([]);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getCountingPlans();
      setPlans(result.rows || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać listy planów.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, [refreshKey]);

  const handleToggleReport = async (planId) => {
    if (expandedPlanId === planId) {
      setExpandedPlanId(null);
      setReport(null);
      return;
    }

    setExpandedPlanId(planId);
    setReport(null);
    setReportLoading(true);
    setError(null);

    try {
      const result = await getCountingPlanReport(planId);
      setReport(result);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać raportu planu.',
      );
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadExcel = async (planId) => {
    try {
      await downloadPlanExcel(planId);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać pliku Excel.',
      );
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Historia planów liczenia
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Komentarz</TableCell>
                  <TableCell>Pozycje</TableCell>
                  <TableCell>Produkty</TableCell>
                  <TableCell>Wykonanie</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Akcje</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {plans.map((plan) => (
                  <React.Fragment key={plan.planBatchId}>
                    <TableRow hover>
                      <TableCell>{plan.plannedAt}</TableCell>
                      <TableCell>{plan.comment || '-'}</TableCell>
                      <TableCell>{plan.itemsCount}</TableCell>
                      <TableCell>{plan.productsCount}</TableCell>
                      <TableCell>{plan.completionPercent}%</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={plan.status}
                          color={statusColor(plan.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleToggleReport(plan.planBatchId)}
                          >
                            {expandedPlanId === plan.planBatchId
                              ? 'Ukryj'
                              : 'Raport'}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleDownloadExcel(plan.planBatchId)
                            }
                          >
                            Excel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0 }}>
                        <Collapse
                          in={expandedPlanId === plan.planBatchId}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            {reportLoading ? (
                              <CircularProgress size={24} />
                            ) : report ? (
                              <PlanReportInline report={report} />
                            ) : null}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}

                {!plans.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Brak zapisanych planów
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

function PlanReportInline({ report }) {
  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2">
          Biny: <strong>{report.summary.totalBins}</strong>
        </Typography>
        <Typography variant="body2">
          Zamknięte: <strong>{report.summary.closedBins}</strong>
        </Typography>
        <Typography variant="body2">
          Otwarte: <strong>{report.summary.openBins}</strong>
        </Typography>
        <Typography variant="body2">
          Realizacja: <strong>{report.summary.completionPercent}%</strong>
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produkt</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell>Bin</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Wynik liczenia</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {report.details.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.product}</TableCell>
                <TableCell>{row.description || '-'}</TableCell>
                <TableCell>{row.bin}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={row.status === 'CLOSED' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {row.countedProducts?.length
                    ? row.countedProducts
                        .map((p) =>
                          p.emptyBinFlag
                            ? 'Pusty bin'
                            : p.product || 'Brak produktu',
                        )
                        .join(', ')
                    : '-'}
                </TableCell>
              </TableRow>
            ))}

            {!report.details.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Brak szczegółów
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
